import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle2,
  Clapperboard,
  Clock,
  Facebook,
  Gamepad2,
  Hotel,
  Instagram,
  Mail,
  Megaphone,
  Music,
  PartyPopper,
  Plane,
  Sparkles,
  Tag,
  Twitter,
  UtensilsCrossed,
  Wallet,
  CalendarCheck,
} from 'lucide-react';
import {
  ButtonLink,
  Card,
  CardGrid,
  Container,
  FAQ,
  FeatureCard,
  IconChip,
  Lead,
  PageHero,
  Reveal,
  Section,
  SectionHeader,
  Steps,
} from '../components/page';
import { Glow } from '../components/ui';
import { EASE } from '../components/motion';
import SmartLink from '../components/SmartLink';

const PARTNERS = [
  { icon: UtensilsCrossed, accent: 'amber', title: 'Restaurants', text: 'Enjoy discounts on group dining at your favorite eateries—think family-style meals or team celebrations.' },
  { icon: Hotel, accent: 'blue', title: 'Hotels', text: 'Save on accommodations for your next group vacation or retreat.' },
  { icon: Plane, accent: 'brand', title: 'Flights', text: 'Get deals on airfare for that long-awaited group trip abroad.' },
  { icon: Clapperboard, accent: 'rose', title: 'Movies & Shows', text: 'Catch the latest blockbuster or a live theater performance at reduced rates with your crew.' },
  { icon: Gamepad2, accent: 'emerald', title: 'Games', text: 'Score discounts on group gaming experiences, from escape rooms to arcade nights.' },
  { icon: Music, accent: 'violet', title: 'Music Festivals', text: 'Access special pricing for tickets to the hottest music events of the year.' },
];

const STEPS = [
  { icon: Wallet, title: 'Pool Your Funds', text: 'Contribute to your group account as usual.' },
  { icon: CalendarCheck, title: 'Plan Your Activity', text: 'Use our tools (and soon, AI Suggestions!) to decide on your group goal—dinner, a trip, or a festival.' },
  { icon: Tag, title: 'Unlock Savings', text: 'When spending from your group pool, look for partner deals (e.g., "$10 off your meal" or "20% off hotel stays") and apply them at checkout.' },
  { icon: PartyPopper, title: 'Enjoy Together', text: 'Spend less, experience more, and keep the group vibes strong!' },
];

const EMAIL = 'support@groupmoney.app';

const FAQS = [
  {
    q: 'When will Group Discounts be available?',
    a: "We're aiming to launch soon, but an exact date isn't set yet. Keep an eye on our updates for the big reveal!",
  },
  {
    q: 'Will Group Discounts be available on all plans?',
    a: "Yes, once live, they'll be included in the Basic and Max tiers. Free tier users can upgrade to access this perk.",
  },
  {
    q: 'Can I suggest a partner business?',
    a: (
      <>
        Absolutely! Email us at{' '}
        <a href={`mailto:${EMAIL}`} className="font-medium text-brand-300 hover:text-white">
          {EMAIL}
        </a>{' '}
        with your ideas—we'd love to hear them.
      </>
    ),
  },
  {
    q: 'What if a deal expires before we use it?',
    a: "We'll work with partners to offer fresh deals regularly. Details on expiration and renewal will be available at launch.",
  },
];

const SOCIALS = [
  { icon: Twitter, label: 'Twitter', href: 'https://twitter.com' },
  { icon: Facebook, label: 'Facebook', href: 'https://facebook.com' },
  { icon: Instagram, label: 'Instagram', href: 'https://instagram.com' },
];

/* Animated "work in progress" status card shown under the hero. */
const ComingSoonCard = () => (
  <Reveal className="relative mx-auto max-w-xl">
    <Glow className="-inset-8 bg-amber-500/10" />
    <div className="surface relative !bg-ink-900/90 p-6 sm:p-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/50 to-transparent" />
      <div className="flex items-start gap-4">
        <IconChip icon={Clock} accent="amber" />
        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xl font-bold tracking-tight text-white">Coming Soon</h3>
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60 motion-reduce:hidden" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
            </span>
          </div>
          <p className="mt-1 text-slate-400">We're finalizing partnerships to bring you the best deals!</p>
        </div>
      </div>
      <div className="mt-6 h-2 overflow-hidden rounded-full bg-white/[0.06]">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500"
          initial={{ width: 0 }}
          whileInView={{ width: '72%' }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
        />
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {['$10 off your meal', '20% off hotel stays'].map((deal) => (
          <span
            key={deal}
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-white/15 px-3 py-1 text-xs font-medium text-slate-400"
          >
            <Tag className="h-3.5 w-3.5 text-amber-300" />
            {deal}
          </span>
        ))}
      </div>
    </div>
  </Reveal>
);

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    setEmail('');
  };

  return (
    <form onSubmit={submit} className="mt-6">
      <label htmlFor="newsletter-email" className="sr-only">
        Email
      </label>
      <div className="flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
        <input
          id="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          className="min-w-0 flex-1 rounded-full border border-white/10 bg-ink-900/80 px-5 py-3 text-base text-white placeholder:text-slate-600 transition-colors hover:border-white/20 focus:border-brand-400/60 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
        <button type="submit" className="btn-primary !px-5 !py-3 !text-sm">
          Subscribe Now
        </button>
      </div>
      <div aria-live="polite">
        <AnimatePresence>
          {sent && (
            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-3 flex items-center gap-2 text-sm font-medium text-emerald-300"
            >
              <CheckCircle2 className="h-4 w-4" />
              You're subscribed! We'll keep you posted.
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </form>
  );
};

const GroupDiscountsPage = () => (
  <>
    <PageHero
      eyebrow="Coming Soon! We're Working on It!"
      highlight="Group Discounts"
      title="Save Big with Your Group!"
      description="At Groupmoney, we're all about making group financial management easy, transparent, and—soon—even more rewarding. We're excited to announce that we're rolling out Group Discounts, a feature that will let your group save money on amazing experiences and essentials through exclusive partnerships with top businesses. While this service isn't available just yet, we're hard at work to bring it to you soon. Stay tuned for updates!"
    />

    <Container className="pb-8">
      <ComingSoonCard />
    </Container>

    <Section>
      <Container>
        <SectionHeader eyebrow="The Idea" title="What Are Group Discounts?" />
        <Lead className="mt-8">
          Imagine pooling your money for a trip, a night out, or a special event—and then saving extra cash just because you're a Groupmoney member. That's what Group Discounts are all about! By partnering with businesses across various industries, we'll offer your group exclusive deals to stretch your funds further. Whether you're planning a getaway or a fun night in, these savings will make every group goal feel like a win.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Partners"
          title="Who We're Partnering With"
          description="We're teaming up with a wide range of businesses to bring you unbeatable offers. Here's a sneak peek at the types of partners you can expect:"
        />
        <CardGrid cols={3} className="mt-16">
          {PARTNERS.map((p) => (
            <FeatureCard
              key={p.title}
              icon={p.icon}
              accent={p.accent}
              title={p.title}
              footer={
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-300/80">
                  <Clock className="h-3.5 w-3.5" />
                  Coming Soon
                </span>
              }
            >
              {p.text}
            </FeatureCard>
          ))}
        </CardGrid>
        <Reveal className="mx-auto mt-12 max-w-2xl text-center">
          <p className="text-lg leading-relaxed text-slate-300">
            These partnerships are still in development, and we'll announce specific names as they join the Groupmoney family. Get ready for a lineup that'll make your group adventures even more affordable!
          </p>
        </Reveal>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Simple Process"
          title="How It Works"
          description="Once live, using Group Discounts will be a breeze:"
        />
        <Steps
          className="mt-16"
          steps={STEPS.map(({ icon: Icon, ...step }) => ({
            ...step,
            extra: <Icon className="mt-5 h-5 w-5 text-brand-300" />,
          }))}
        />
        <Reveal className="mx-auto mt-12 max-w-2xl text-center">
          <p className="text-lg leading-relaxed text-slate-300">
            We're designing this feature to integrate seamlessly with your existing{' '}
            <SmartLink to="/group-controls" className="font-medium text-brand-300 hover:text-white">
              "Group Controls"
            </SmartLink>{' '}
            rules, ensuring everyone agrees on the fun—and the savings.
          </p>
        </Reveal>
      </Container>
    </Section>

    <Section>
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-5 lg:gap-16">
          <div className="lg:col-span-3">
            <SectionHeader align="left" eyebrow="Our Why" title="Why We're Excited" />
            <Reveal className="mt-6">
              <p className="text-lg leading-relaxed text-slate-400 sm:text-xl sm:leading-relaxed">
                Group Discounts are more than just savings—they're a way to reward your group for coming together. Whether it's a family saving for a holiday, roommates treating themselves to a movie night, or friends hitting a music festival, these deals will add value to every dollar you pool. Plus, they align with our mission to make group collaboration worry-free and enjoyable.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.12} className="relative lg:col-span-2">
            <Glow className="-inset-8 bg-brand-500/15" />
            <div className="surface relative !border-brand-400/25 p-8 text-center sm:p-10">
              <IconChip icon={Sparkles} size="lg" className="mx-auto" />
              <p className="mt-6 text-2xl font-bold tracking-tight text-white">Ready to Save?</p>
              <p className="mt-2 text-slate-400">Join now and be the first to know when Group Discounts launch!</p>
              <ButtonLink to="/#get-started" className="mt-8 w-full">
                Sign Up Today
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Updates"
          title="Stay in the Loop"
          description="We're working diligently to bring Group Discounts to life, and we can't wait to share them with you. Here's how to stay updated:"
        />
        <CardGrid cols={3} className="mt-16">
          <Card hover={false}>
            <IconChip icon={Megaphone} accent="brand" />
            <h3 className="mt-6 text-xl font-bold tracking-tight text-white">Join Our Newsletter</h3>
            <p className="mt-3 leading-relaxed text-slate-400">Sign up for the latest news on launch dates and early access.</p>
            <NewsletterForm />
          </Card>
          <Card hover={false}>
            <IconChip icon={Sparkles} accent="violet" />
            <h3 className="mt-6 text-xl font-bold tracking-tight text-white">Follow Us</h3>
            <p className="mt-3 flex-1 leading-relaxed text-slate-400">Check our social media for sneak peeks and partner announcements.</p>
            <div className="mt-6 flex gap-2">
              {SOCIALS.map(({ icon: Icon, label, href }) => (
                <SmartLink
                  key={label}
                  to={href}
                  aria-label={label}
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/[0.08] text-slate-400 transition-colors duration-200 hover:border-white/20 hover:text-white"
                >
                  <Icon className="h-5 w-5" />
                </SmartLink>
              ))}
            </div>
          </Card>
          <Card hover={false}>
            <IconChip icon={Mail} accent="emerald" />
            <h3 className="mt-6 text-xl font-bold tracking-tight text-white">Contact Us</h3>
            <p className="mt-3 flex-1 leading-relaxed text-slate-400">
              Have ideas for partners or questions? Drop us a line at {EMAIL}.
            </p>
            <ButtonLink to={`mailto:${EMAIL}`} variant="secondary" className="mt-6 self-start">
              Email Us
            </ButtonLink>
          </Card>
        </CardGrid>
        <Reveal className="mx-auto mt-12 max-w-2xl text-center">
          <p className="text-lg leading-relaxed text-slate-300">
            In the meantime, start your group, set your goals, and get ready to save big when Group Discounts go live!
          </p>
        </Reveal>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader eyebrow="FAQ" title="Frequently Asked Questions" />
        <FAQ items={FAQS} className="mt-16" />
        <Reveal className="mt-12 text-center">
          <p className="text-slate-400">Have more questions? We're happy to help!</p>
          <ButtonLink to="/contact" variant="secondary" className="mt-6">
            Contact Support
          </ButtonLink>
        </Reveal>
      </Container>
    </Section>
  </>
);

export default GroupDiscountsPage;
